import CustomCollapsible from "@/components/ui/collapsibleCard/collapsible-card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
} from "react-hook-form";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { Product, ProductType } from "@/types/system-setting/product";
import { status } from "@/lib/constants/global";
import { useGetIncomeCategoryQuery } from "@/services/incomeCategoryApi";
import { useGetTaxGroupQuery } from "@/services/salesTaxApi";
import { useMemo, useState, useEffect } from "react";
import Combobox from "@/components/ui/common/combobox";
import { TaxGroupType } from "@/types/enums";
import { useGetPriceMastersQuery } from "@/services/priceMasterApi";
import { PriceMaster } from "@/types/system-setting/priceMaster";
import { Eye } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import PriceMasterForm from "../../price_master/modal/form";
import { Button } from "@/components/ui/button";

interface ProductPriceSectionProp {
  control: Control<Product>;
  errors: FieldErrors<Product>;
  watch: UseFormWatch<Product>;
  setValue: UseFormSetValue<Product>;
  getValues: UseFormGetValues<Product>;
  register: UseFormRegister<Product>;
  trigger: UseFormTrigger<Product>;
}

const ProductPriceSection = ({
  control,
  errors,
  watch,
  setValue,
  getValues,
  register,
  trigger,
}: ProductPriceSectionProp) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { data: incomeCategoryData } = useGetIncomeCategoryQuery(
    { org_id: orgId, query: "sort_key=id&sort_order=desc&status=active" },
    {
      skip: !orgId,
    });


  const { data: taxGroupData } = useGetTaxGroupQuery({ query: "", org_id: orgId });

  const { data: priceMasterData, isLoading: priceMasterLoading } =
    useGetPriceMastersQuery(
      { query: "item_type=product", org_id: orgId },
      { skip: !orgId }
    );

  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedRuleData, setSelectedRuleData] = useState<PriceMaster | null>(
    null
  );

  const watcher = watch();

  // Hide income category and tax group when product type is Wallet
  useEffect(() => {
    if (watcher.type === ProductType.Wallet) {
      setValue("income_category_id", undefined);
      setValue("tax_group_id", undefined);
    }
  }, [watcher.type, setValue]);
  
  // Trigger cost validation when price master changes
  useEffect(() => {
    if (watcher.price_master_id) {
      trigger("cost");
    }
  }, [watcher.price_master_id, trigger]);

  const incomeCatOptions = useMemo(() => {
    return Array.isArray(incomeCategoryData?.data)
      ? incomeCategoryData?.data.map((incomeCat) => ({
        ...incomeCat,
        label: incomeCat?.name || "",
        value: incomeCat?.id?.toString() || "",
      }))
      : [];
  }, [incomeCategoryData]);

  const taxGroupOptions = useMemo(() => {
    return Array.isArray(taxGroupData?.data)
      ? taxGroupData?.data.filter((tg) => tg.type === TaxGroupType.Item).map((taxGroup) => ({
        ...taxGroup,
        label: taxGroup?.name || "",
        value: taxGroup?.id?.toString() || "",
      }))
      : [];
  }, [taxGroupData]);

  const priceMasterOptions = useMemo(() => {
    return priceMasterData && Array.isArray(priceMasterData.data)
      ? priceMasterData.data.map((rule) => ({
          label: `${rule.rule_code} : ${rule.rule_name}`,
          subLabel: rule.rule_name,
          value: String(rule.id),
        }))
      : [];
  }, [priceMasterData]);

  const handlePreview = (ruleId: number) => {
    const rule = priceMasterData?.data?.find(
      (r) => r.id === ruleId
    );
    if (rule) {
      setSelectedRuleData(rule);
      setPreviewOpen(true);
    }
  };

  return (
    <div className="rounded-2xl bg-white">
      <CustomCollapsible
        title="Price Details"
        isOpenDefault={true}
        hideToggle={false}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* <div>
            <p className="text-sm font-medium mb-1.5">
              Product Type <span className="text-red-500">*</span>
            </p>
            <Controller
              control={control}
              name="type"
              rules={{ required: "Required" }}
              defaultValue={watcher.type}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Product Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ProductType.Wallet}>Wallet</SelectItem>
                    <SelectItem value={ProductType.OtherProduct}>Other Product</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.type?.message && (
              <p className="text-red-500 text-xs mt-1">
                {errors.type.message.toString()}
              </p>
            )}
          </div> */}

          {watcher.type !== ProductType.Wallet && (
            <div>
              <p className="text-sm font-medium mb-1.5">
                Cost<span className="text-red-500">*</span>
              </p>
              <Controller
                control={control}
                name="cost"
                rules={{
                  required: "Required",
                  pattern: {
                    value: /^\d+$/,
                    message: "Only numbers are allowed",
                  },
                  validate: (value) => {
                    const priceMasterId = watch("price_master_id");
                    if (!priceMasterId) return true;
                    
                    const selectedRule = (priceMasterData as any)?.data?.find(
                      (r: any) => r.id === Number(priceMasterId)
                    );
                    
                    if (selectedRule && Number(value) > selectedRule.price) {
                      return `Cost cannot be greater than the selling price (Rs. ${selectedRule.price})`;
                    }
                    return true;
                  }
                }}
                render={({ field }) => (
                  <Input
                    type="number"
                    className="w-full h-9"
                    placeholder="Enter Product Cost"
                    {...field}
                    onChange={(e) => {
                      if (/^\d*$/.test(e.target.value)) {
                        field.onChange(e);
                        trigger("price");
                      }
                    }}
                  />
                )}
              />
              {errors.cost?.message && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.cost.message.toString()}
                </p>
              )}
            </div>
          )}


          {watcher.type == ProductType.Wallet && <div>
            <p className="text-sm font-medium mb-1.5">
              Flexible Price <span className="text-red-500">*</span>
            </p>
            <Controller
              control={control}
              name="flexible_price"
              render={({ field }) => (
                <div className="flex gap-2 h-fit my-3 items-center">
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />
          </div>}
          {!watcher.flexible_price && (
            <div>
              <p className="text-sm font-medium mb-1.5">
                Price Master Rule <span className="text-red-500">*</span>
              </p>
              <Controller
                control={control}
                name="price_master_id"
                rules={{ required: "Required" }}
                render={({ field }) => (
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Combobox
                        list={priceMasterOptions}
                        defaultValue={
                          priceMasterOptions.find(
                            (opt: any) => opt.value === String(field.value)
                          )?.label || ""
                        }
                        setFilter={(selectedLabel: string) => {
                          const selectedOption = priceMasterOptions.find(
                            (opt: any) => opt.label === selectedLabel
                          );
                          field.onChange(
                            selectedOption
                              ? Number(selectedOption.value)
                              : undefined
                          );
                        }}
                        label="Select Rule"
                        showLabel={false}
                        disabled={priceMasterLoading}
                        className="h-9"
                      />
                    </div>
                    {field.value && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-indigo-600 hover:bg-indigo-50 border border-indigo-100 shrink-0"
                        onClick={() => handlePreview(Number(field.value))}
                        title="Preview details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              />
              {errors.price_master_id?.message && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.price_master_id.message.toString()}
                </p>
              )}
            </div>
          )}

          {watcher.type !== ProductType.Wallet && (
            <>
              <div>
                <p className="text-sm font-medium mb-1.5">
                  Manage Inventory <span className="text-red-500">*</span>
                </p>
                <Controller
                  control={control}
                  name="manage_inventory"
                  render={({ field }) => (
                    <div className="flex gap-2 h-fit my-3 items-center">
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </div>
                  )}
                />
                {errors.manage_inventory?.message && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.manage_inventory.message.toString()}
                  </p>
                )}
              </div>

              <div>
                <p className="text-sm font-medium mb-1.5">
                  Inventory<span className="text-red-500">*</span>
                </p>
                <Controller
                  control={control}
                  name="quantity"
                  rules={{
                    required: "Required",
                    pattern: {
                      value: /^\d+$/,
                      message: "Only numbers are allowed",

                    },
                    max: {
                      value: 10000,
                      message: "Quantity cannot be greater than 10000",
                    }
                  }}
                  render={({ field }) => (
                    <Input
                      type="number"
                      className="w-full h-9"
                      placeholder="Enter Product Quantity"
                      {...field}
                      onChange={(e) => {
                        if (/^\d*$/.test(e.target.value)) {
                          field.onChange(e);
                        }
                      }}
                    />
                  )}
                />
                {errors.quantity?.message && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.quantity.message.toString()}
                  </p>
                )}
              </div>
              
              <div>
                <p className="text-sm font-medium mb-1.5">
                  Min Limit<span className="text-red-500">*</span>
                </p>
                <Controller
                  control={control}
                  name="min_limit"
                  rules={{
                    required: "Required",
                    pattern: {
                      value: /^\d+$/,
                      message: "Only numbers are allowed",
                    },
                    max: {
                      value: 1000,
                      message: "Min Limit cannot be greater than 1000",
                    }
                  }}
                  render={({ field }) => (
                    <Input
                      type="number"
                      className="w-full h-9"
                      placeholder="Enter Min Limit"
                      {...field}
                      onChange={(e) => {
                        if (/^\d*$/.test(e.target.value)) {
                          field.onChange(e);
                        }
                      }}
                    />
                  )}
                />
                {errors.min_limit?.message && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.min_limit.message.toString()}
                  </p>
                )}
              </div>
            </>
          )}

          {watcher.type !== ProductType.Wallet && (
            <>
              <div>
                <p className="text-sm font-medium mb-1.5">
                  Income Category <span className="text-red-500">*</span>
                </p>
                <Controller
                  name="income_category_id"
                  rules={{ required: watcher.type == ProductType.OtherProduct && "Required" }}
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <>
                      <Combobox
                        list={incomeCatOptions}
                        defaultValue={
                          incomeCatOptions?.find(
                            (option) => option.value === value?.toString()
                          )?.label || ""
                        }
                        setFilter={(selectedLabel: string) => {
                          const selectedOption = incomeCatOptions?.find(
                            (option) => option.label === selectedLabel
                          );
                          onChange(
                            selectedOption
                              ? Number(selectedOption.value)
                              : undefined
                          );
                        }}
                        label="Income Category"
                        showLabel={false}
                        className="h-9"
                      />
                      {error && (
                        <span className="text-red-500 text-xs mt-[5px]">
                          {error.message}
                        </span>
                      )}
                    </>
                  )}
                />
              </div>

              <div>
                <p className="text-sm font-medium mb-1.5">
                  Tax Group <span className="text-red-500">*</span>
                </p>
                <Controller
                  name="tax_group_id"
                  rules={{ required: watcher.type == ProductType.OtherProduct && "Required" }}
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <>
                      <Combobox
                        list={taxGroupOptions}
                        defaultValue={
                          taxGroupOptions?.find(
                            (option) => option.value === value?.toString()
                          )?.label || ""
                        }
                        setFilter={(selectedLabel: string) => {
                          const selectedOption = taxGroupOptions?.find(
                            (option) => option.label === selectedLabel
                          );
                          onChange(
                            selectedOption
                              ? Number(selectedOption.value)
                              : undefined
                          );
                        }}
                        label="Tax Group"
                        showLabel={false}
                        className="h-9"
                      />
                      {error && (
                        <span className="text-red-500 text-xs mt-[5px]">
                          {error.message}
                        </span>
                      )}
                    </>
                  )}
                />
              </div>
            </>
          )}


          <div>
            <p className="text-sm font-medium mb-1.5">
              Status <span className="text-red-500">*</span>
            </p>
            <Controller
              name="status"
              rules={{ required: "Required" }}
              control={control}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { invalid, error },
              }) => {
                const statusLabel = status.filter((r) => r.value == value)[0];
                return (
                  <Select
                    onValueChange={(value) => onChange(value)}
                    defaultValue={value}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue
                        placeholder="Select status"
                        className="text-gray-400 "
                      >
                        <span className="flex gap-2 items-center">
                          <span
                            className={`${statusLabel?.color} rounded-[50%] w-4 h-4`}
                          ></span>
                          <span>{statusLabel?.label}</span>
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {status.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }}
            />
            {errors.status?.message && (
              <p className="text-red-500 text-xs mt-1">
                {errors.status.message.toString()}
              </p>
            )}
          </div>
        </div>
      </CustomCollapsible>

      {/* Price Master Preview Modal */}
      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent className="overflow-y-auto max-w-full sm:max-w-[700px] w-full p-0">
          <SheetHeader className="p-6 border-b bg-indigo-50/50">
            <SheetTitle className="text-xl font-bold text-indigo-900">
              Pricing Rule Preview
            </SheetTitle>
            <SheetDescription className="text-indigo-600">
              Viewing pre-configured pricing rule details
            </SheetDescription>
          </SheetHeader>
          <div className="p-6">
            {selectedRuleData && (
              <PriceMasterForm
                isOpen={previewOpen}
                setOpen={setPreviewOpen}
                action="edit"
                data={selectedRuleData}
                readOnly={true}
                setData={() => {}}
                setAction={() => {}}
                refetch={() => {}}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ProductPriceSection;